import React, { ChangeEvent, FC, Fragment, useState } from 'react';
import Auth from '@aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import { Button, Typography } from '@material-ui/core';
import { FieldProps, Form, Formik } from 'formik';

import { useNotify, usePublicClient } from 'hooks';
import { Login } from 'Links';
import {
  ActionContainer,
  ClientLogo,
  FormikEffect,
  Layouts,
  Logo,
  SubmitButton,
  ValidatedTextField,
} from 'components';

import { Main } from '../components';
import { ForgotPasswordSendSchema, IForgotPasswordSendValues } from '../validations';
import { IAuthContainerProps } from '../types';

const ForgotPassword: FC<IAuthContainerProps> = ({ authData, updateUsername }) => {
  const notify = useNotify();
  const client = usePublicClient();
  const { t } = useTranslation();
  const [working, setWorking] = useState(false);

  const initialValues: IForgotPasswordSendValues = {
    username: authData.username,
  };

  function handleChange({ username }: IForgotPasswordSendValues) {
    updateUsername(username.toLowerCase());
  }

  async function handleSend(values: IForgotPasswordSendValues) {
    setWorking(true);

    try {
      await Auth.forgotPassword(values.username.toLowerCase());
    } catch (error) {
      notify.error(error);
      setWorking(false);
    }
  }

  function renderSidebar() {
    return (
      <Fragment>
        {client ? <ClientLogo client={client} /> : <Logo />}
        <Typography gutterBottom variant="h4" color="textSecondary">
          {t('auth:forgotPasswordSidebarHeader')}
        </Typography>
        <Formik
          key="send"
          onSubmit={handleSend}
          initialValues={initialValues}
          validationSchema={ForgotPasswordSendSchema}
        >
          <Form>
            <FormikEffect onChange={handleChange} delay={100} />
            <ValidatedTextField
              name="username"
              i18nKey="user:email"
              onChange={(
                event: ChangeEvent<HTMLInputElement>,
                onChange: FieldProps['field']['onChange']
              ) => {
                event.target.value = event.target.value.trim();
                onChange(event);
              }}
            />
            <ActionContainer>
              <SubmitButton working={working} text={t('auth:forgotPasswordSendButton')} />
              <Button component={Login} fullWidth>
                {t('auth:backToLogin')}
              </Button>
            </ActionContainer>
          </Form>
        </Formik>
      </Fragment>
    );
  }

  return (
    <Layouts.Full2Column
      sidebar={renderSidebar()}
      main={<Main headerI18nKey="auth:bodyHeader1" subHeaderI18nKey="auth:signInSubHeader" />}
    />
  );
};

export default ForgotPassword;
